import React, { useMemo, useState } from 'react';
import {
  ChevronDown, ChevronUp, Search, Loader2, AlertCircle, CircleHelp,
} from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import { useSiteContent } from '../../hooks/useSiteContent';

const SiteFAQPage = () => {
  const { page, loading, error } = useSiteContent('faq');
  const [searchQuery, setSearchQuery] = useState('');
  const [openItems, setOpenItems] = useState({});

  const sections = page?.content_json?.sections || [];

  const filteredSections = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return sections;
    return sections
      .map((section) => ({
        ...section,
        items: (section.items || []).filter(
          (item) =>
            item.question?.toLowerCase().includes(q)
            || String(item.answer_html || '').toLowerCase().includes(q)
        ),
      }))
      .filter((section) => section.items?.length);
  }, [sections, searchQuery]);

  const toggleItem = (key) => {
    setOpenItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50/40 via-white to-pink-50/30">
      <Header />

      <div className="max-w-4xl mx-auto px-4 py-12 pt-24">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-purple-100 mb-4">
            <CircleHelp className="w-7 h-7 text-purple-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-purple-700 mb-3">
            {page?.title || 'FAQ'}
          </h1>
          {page?.subtitle && <p className="text-gray-600">{page.subtitle}</p>}
        </div>

        <div className="relative mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search questions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500"
          />
        </div>

        {loading && (
          <div className="flex justify-center py-16">
            <Loader2 className="h-10 w-10 animate-spin text-purple-600" />
          </div>
        )}

        {!loading && error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700">{error}</div>
        )}

        {!loading && !error && (
          <div className="space-y-8">
            {filteredSections.map((section) => (
              <div key={section.key || section.title} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="bg-purple-100 px-6 py-4">
                  <h2 className="text-lg font-bold text-purple-900">{section.title}</h2>
                </div>
                <div className="divide-y divide-gray-100">
                  {(section.items || []).map((item, index) => {
                    const itemKey = `${section.key || section.title}-${index}`;
                    const isOpen = !!openItems[itemKey];
                    return (
                      <div key={itemKey}>
                        <button
                          type="button"
                          onClick={() => toggleItem(itemKey)}
                          className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50"
                        >
                          <span className="font-semibold text-gray-900 pr-4">{item.question}</span>
                          {isOpen ? (
                            <ChevronUp className="w-5 h-5 text-purple-600 shrink-0" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-purple-600 shrink-0" />
                          )}
                        </button>
                        {isOpen && (
                          <div
                            className="px-6 pb-5 text-gray-700 leading-8 site-content-html"
                            dangerouslySetInnerHTML={{ __html: item.answer_html || '' }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default SiteFAQPage;
